import type {
  PluginComponentOption,
  PluginDefinition,
  PluginOfferings,
  ResellerPluginConfig,
} from "./types";

import { FREE_TEXT_DEFINITION } from "./plugins/free_text/definition";
import { PLAIN_MESSAGE_DEFINITION } from "./plugins/plain_message/definition";
import { QUICK_CHECK_DEFINITION } from "./plugins/quick_check/definition";
import { ROLE_PLAY_DEFINITION } from "./plugins/role_play/definition";

/** The single component type every sub_type is hosted by. */
const PLUGIN_TEMPLATE = "plugin_component" as const;

/**
 * Every activity variant hosted by the `plugin_component` component type.
 * Keep in sync with edrna_backend/plugin_components/plugins/.
 */
const DEFINITIONS: PluginDefinition[] = [
  FREE_TEXT_DEFINITION,
  QUICK_CHECK_DEFINITION,
  PLAIN_MESSAGE_DEFINITION,
  ROLE_PLAY_DEFINITION,
];

const BY_SUB_TYPE: Record<string, PluginDefinition> = Object.fromEntries(
  DEFINITIONS.map((definition) => [definition.subType, definition])
);

/** Returns null for unknown sub_types so callers can render a placeholder. */
export const getPlugin = (subType: string | undefined | null): PluginDefinition | null =>
  (subType && BY_SUB_TYPE[subType]) || null;

export const allPlugins = (): PluginDefinition[] => DEFINITIONS;

export const allowedPlugins = (config?: ResellerPluginConfig | null): PluginDefinition[] => {
  const allowList = config?.allow_list ?? [];

  return DEFINITIONS.filter((definition) => allowList.includes(definition.subType));
};

/** `getPlugin`, narrowed to what this reseller allows. Null reads as "unavailable". */
export const getAllowedPlugin = (
  subType: string | undefined | null,
  config?: ResellerPluginConfig | null
): PluginDefinition | null => {
  const plugin = getPlugin(subType);
  const allowList = config?.allow_list ?? [];

  return plugin && allowList.includes(plugin.subType) ? plugin : null;
};

const optionsOf = (definition: PluginDefinition): Promise<PluginOfferings> =>
  definition
    .offeredComponents()
    .then((offers) => ({
      options: offers.map(
        (offer): PluginComponentOption => ({
          ...offer,
          template: PLUGIN_TEMPLATE,
          sub_type: definition.subType,
        })
      ),
      unavailable: [],
    }))
    .catch((err: unknown) => {
      console.error(`plugin "${definition.subType}" could not list its components`, err);

      return { options: [], unavailable: [definition.subType] };
    });

const mergeOfferings = (offerings: PluginOfferings[]): PluginOfferings => ({
  options: offerings.flatMap(({ options }) => options),
  unavailable: offerings.flatMap(({ unavailable }) => unavailable),
});

/** Components these plugins offer. */
export const collectOfferings = (definitions: PluginDefinition[]): Promise<PluginOfferings> =>
  Promise.all(definitions.map(optionsOf)).then(mergeOfferings);

/** Every plugin component the author's new-component picker should show. */
export const offeredComponents = (): Promise<PluginOfferings> => collectOfferings(DEFINITIONS);
